import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionBids } from 'dibs-auctions/exports/hooks';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import classnames from 'classnames';
// components
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedAuctionActionBidLabel } from './PdpSharedAuctionActionBidLabel';
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';
import { PdpSharedAuctionBidsDetails } from '../PdpSharedAuctionBidsDetails/PdpSharedAuctionBidsDetails';
import { OriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { PdpSharedAuctionCountdown } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdown';

// types
import { PdpSharedAuctionActionBid_item$key as Item } from './__generated__/PdpSharedAuctionActionBid_item.graphql';
import { OnAuctionBidClick } from '../PdpSharedAuctionInfo/PdpSharedAuctionInfo';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpSharedAuctionActionBid.scss';
import variantStyles from './PdpSharedAuctionActionsTestVariant.scss';

type Props = {
    item: Item;
    currency: string;
    isBiddingDisabled?: boolean;
    onAuctionBidClick: OnAuctionBidClick;
    isCloseToReserve: boolean;
    isAuctionSiderailRedesignVariant?: boolean | null;
};

type RelayVariables = {
    userId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const increaseBidLabel = (
    <FormattedMessage id="PdpSharedAuctionActionBid.increaseBid" defaultMessage="Increase Bid" />
);

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBid_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionBids_item @arguments(fetchUser: $fetchUser)
        ...PdpSharedAuctionBidsDetails_item
        ...PdpSharedAuctionActionBidLabel_item
        ...OriginalListPrice_item
        ...PdpSharedAuctionCountdown_item
    }
`;

const copy = {
    currentBid: (
        <FormattedMessage id="PdpSharedAuctionActionBid.currentBid" defaultMessage="Current Bid" />
    ),
};

export const PdpSharedAuctionActionBid: FC<Props> = ({
    item: itemRef,
    currency,
    onAuctionBidClick,
    isBiddingDisabled,
    isCloseToReserve,
    isAuctionSiderailRedesignVariant,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { isMobile } = useServerVarsContext();
    const { userId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { hasBid } = useAuctionBids({ item, userId, currency });

    if (isAuctionSiderailRedesignVariant) {
        return (
            <div>
                <div className={variantStyles.infoGroupContainer}>
                    {!isMobile && (
                        <div>
                            <PdpSharedAuctionCountdown
                                isAuctionSiderailRedesignVariant
                                item={item}
                            />
                        </div>
                    )}
                    <div>
                        <OriginalListPrice
                            item={item}
                            currency={currency}
                            isAuctionSidebarTestVariant
                            className={classnames(variantStyles.originalListPriceVariant, {
                                [dibsCss.sassyFontBodyMediumHeavy]: isMobile,
                                [dibsCss.sassyFontBodySmallHeavy]: !isMobile,
                            })}
                        />
                    </div>
                </div>
                <div className={variantStyles.bidContainer}>
                    <PdpSharedAuctionActionBidLabel
                        isAuctionSiderailRedesignVariant
                        item={item}
                        currency={currency}
                        isCloseToReserve={isCloseToReserve}
                        className={classnames(variantStyles.bidLabel, {
                            [dibsCss.sassyFontBodyMedium]: isMobile,
                            [dibsCss.sassyFontBodySmall]: !isMobile,
                        })}
                    />
                    <div>
                        <div className={variantStyles.infoGroup}>
                            <div className={variantStyles.infoHeader}>{copy.currentBid}</div>
                            <div className={variantStyles.infoDetail}>
                                <PriceDisplay
                                    currency={currency}
                                    item={item}
                                    sku={null}
                                    useSvgIcon
                                />
                            </div>
                        </div>
                        <PdpSharedAuctionBidsDetails
                            isAuctionSiderailRedesignVariant
                            item={item}
                            currency={currency}
                            className={variantStyles.bidDetails}
                        />
                        <div className={styles.printHide}>
                            <PdpSharedAuctionBidButton
                                onClick={() => onAuctionBidClick(hasBid)}
                                disabled={isBiddingDisabled}
                                label={hasBid ? increaseBidLabel : null}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <OriginalListPrice
                item={item}
                currency={currency}
                className={styles.originalListPrice}
            />
            <div className={styles.container}>
                <span className={styles.bid}>{copy.currentBid}</span>
                <div className={styles.priceInfo}>
                    <PriceDisplay currency={currency} item={item} useSvgIcon sku={null} />
                    <PdpSharedAuctionBidsDetails
                        item={item}
                        currency={currency}
                        className={styles.bidDetails}
                    />
                </div>
                <PdpSharedAuctionActionBidLabel
                    item={item}
                    currency={currency}
                    isCloseToReserve={isCloseToReserve}
                />
                <div className={styles.printHide}>
                    <PdpSharedAuctionBidButton
                        onClick={() => onAuctionBidClick(hasBid)}
                        disabled={isBiddingDisabled}
                        label={hasBid ? increaseBidLabel : null}
                    />
                </div>
            </div>
        </>
    );
};
